import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';
import { billingService } from 'services/billingService';
import { RecordPaymentDto } from 'types/models';

const KEY      = 'billing-account';
const REC_KEY  = 'billing-receipts';
const AR_KEY   = 'billing-ar-debtors';
const DAILY_KEY = 'billing-daily-box';

export function useBillingAccount(patientId: string) {
  return useQuery({
    queryKey: [KEY, patientId],
    queryFn:  () => billingService.getAccountByPatient(patientId),
    enabled:  !!patientId,
  });
}

export function useReceipts(patientId: string) {
  return useQuery({
    queryKey: [REC_KEY, patientId],
    queryFn:  () => billingService.getReceipts(patientId),
    enabled:  !!patientId,
  });
}

export function useAccountsWithDebt() {
  return useQuery({
    queryKey: [AR_KEY],
    queryFn:  () => billingService.getAccountsWithDebt(),
    staleTime: 60_000,
  });
}

export function useDailyBox() {
  return useQuery({
    queryKey: [DAILY_KEY],
    queryFn:  () => billingService.getDailyBox(),
    staleTime: 30_000,
  });
}

export function useCloseShift() {
  const toast = useToast();

  return useMutation({
    mutationFn: () => billingService.closeShift(),
    onSuccess: (blob) => {
      const url  = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const date = new Date().toISOString().slice(0, 10);
      link.href     = url;
      link.download = `cierre-turno-${date}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      toast({
        title: 'Cierre de turno confirmado',
        description: 'PDF generado y descargado correctamente.',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message;
      toast({
        title: 'Error al cerrar turno',
        description: Array.isArray(msg) ? msg.join(' · ') : (msg ?? 'Ocurrió un error inesperado.'),
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });
}

export function useRecordPayment() {
  const queryClient = useQueryClient();
  const toast       = useToast();

  return useMutation({
    mutationFn: ({ patientId, payload }: { patientId: string; payload: RecordPaymentDto }) =>
      billingService.recordPayment(patientId, payload),
    onSuccess: (res, vars) => {
      queryClient.invalidateQueries({ queryKey: [KEY,     vars.patientId] });
      queryClient.invalidateQueries({ queryKey: [REC_KEY, vars.patientId] });
      queryClient.invalidateQueries({ queryKey: [AR_KEY] });
      const debt = Number(res.account.balance.currentDebt);
      toast({
        title: 'Pago registrado',
        description: debt === 0
          ? `Recibo ${res.receipt.receiptNumber} — cuenta al día.`
          : `Recibo ${res.receipt.receiptNumber} — deuda restante: $${debt.toFixed(2)}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message;
      toast({
        title: 'Error al registrar pago',
        description: Array.isArray(msg) ? msg.join(' · ') : (msg ?? 'Ocurrió un error inesperado.'),
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });
}
